import React from 'react';
import { Plus, BarChart3, Check, Sparkles } from 'lucide-react';
import { Tooltip } from 'antd';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { Badge, Button } from '../../DashboardV2/components/primitives';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';

const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const monthName = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { month: 'long' });
};

const GoalsHeader = ({
  currentCycle = null,
  bannerConfig = {},
  employeeAvgScore = null,
  months = [],
  goalsCount = 0,
  maxGoals = 0,
  hasGoalsInCurrentCycle = false,
  isEvaluationWindowActive = false,
  areGoalsEvaluated = false,
  isOrgAdmin = false,
  isAutonomousMode = false,
  onMonthClick,
}) => {
  const t = useDashTheme();
  const { settings: goalsSettings } = useGoalsSettings();

  // Org flow mode drives everything mode-aware in this header.
  const flowMode = goalsSettings?.goalsMode || 'classic';
  const isWindowless = flowMode === 'score_only' || flowMode === 'checklist';
  const isStarFlow = flowMode === 'star' || currentCycle?.scoringMode === 'star5';

  const cycleStart = currentCycle?.submissionStart || currentCycle?.startDate || null;
  const cycleMonth = monthName(cycleStart) || (currentCycle?.month || '');
  const cycleYear = cycleStart ? new Date(cycleStart).getFullYear() : (currentCycle?.year || '');

  const cyclePillText = cycleMonth && cycleYear
    ? `${cycleMonth} ${cycleYear} cycle`
    : (bannerConfig?.badge || 'Current cycle');

  const headerTitle = cycleMonth ? `Goals for ${cycleMonth}` : (bannerConfig?.title || 'Goals');

  const hasValidScore = employeeAvgScore != null && parseFloat(employeeAvgScore) > 0;
  const scoreDisplay = hasValidScore
    ? (Math.floor(parseFloat(employeeAvgScore) * 10) / 10).toFixed(1)
    : '0';
  const scoreDenominator = isStarFlow ? 5 : 10;

  // Compute correct end date from a start date + duration (duration days inclusive)
  const computeEndDate = (startDate, duration) => {
    if (!startDate || !duration) return null;
    const d = new Date(startDate);
    d.setDate(d.getDate() + duration - 1);
    return d;
  };

  // Phase steps — plain-language names, end dates derived from settings
  // duration to avoid stale DB values. Only meaningful in cycle modes.
  const steps = (() => {
    if (!currentCycle || isWindowless) return [];
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const defs = [
      { key: 'submission', label: 'Add goals', start: currentCycle.submissionStart, end: currentCycle.submissionEnd, duration: goalsSettings?.submissionWindow?.duration },
      { key: 'review', label: 'Manager approval', start: currentCycle.reviewStart, end: currentCycle.reviewEnd, duration: goalsSettings?.reviewWindow?.duration },
      { key: 'evaluation', label: isStarFlow ? 'Complete & verify' : 'Scoring', start: currentCycle.evaluationStart, end: currentCycle.evaluationEnd, duration: goalsSettings?.evaluationWindow?.duration },
    ];
    return defs
      .filter(d => d.start)
      .map(d => {
        const end = (d.duration && d.start) ? computeEndDate(d.start, d.duration) : d.end;
        const s = new Date(d.start); s.setHours(0, 0, 0, 0);
        const e = end ? new Date(end) : null; if (e) e.setHours(23, 59, 59, 999);
        const state = e && now > e ? 'done' : (now >= s && e && now <= e ? 'active' : 'upcoming');
        const daysLeft = state === 'active' ? Math.max(0, Math.ceil((e - new Date()) / 86400000)) : null;
        return { ...d, end, state, daysLeft };
      });
  })();

  const activeStep = steps.find(s => s.state === 'active') || null;

  // Org admins have no manager above them to approve/score, so the normal
  // submit flow would strand their goals in Pending. They manage goals via
  // "Unlock editing" instead.
  const isAdminLocked = !isWindowless && isOrgAdmin && !isAutonomousMode;

  // "Your move" — the one thing the user should do right now.
  const yourMove = (() => {
    if (flowMode === 'checklist') return 'Add goals and check them off as you finish — no windows, no scores.';
    if (flowMode === 'score_only') return 'Add goals and update scores anytime — no approval windows.';
    if (isAdminLocked) return 'As org admin, your goals skip manager approval — turn on "Unlock editing" (top right) to add and manage them.';
    if (!currentCycle) return null;
    if (activeStep?.key === 'submission') {
      if (goalsCount === 0) return `Add your goals for ${cycleMonth || 'this month'} — closes ${formatDate(activeStep.end)}.`;
      return `${goalsCount}${maxGoals ? `/${maxGoals}` : ''} goals in. You can add or edit until ${formatDate(activeStep.end)}.`;
    }
    if (activeStep?.key === 'review') return 'Your manager is approving goals now. Check back for any that need changes.';
    if (activeStep?.key === 'evaluation') {
      return isStarFlow
        ? 'Mark your goals as done — your manager verifies them and stars are awarded.'
        : 'Scoring is underway — your manager sets what you achieved this month.';
    }
    const next = steps.find(s => s.state === 'upcoming');
    if (next) return `Next up: ${next.label.toLowerCase()} opens ${formatDate(next.start)}.`;
    return 'This cycle is wrapped up. Check your score and get ready for next month.';
  })();

  // Submission window flag (drives CTA disabled/label). Windowless modes are
  // always open — mirrors the same unlock in GoalsTable.
  const isSubmissionWindowOpen = isWindowless || (() => {
    if (!currentCycle?.submissionStart || !currentCycle?.submissionEnd) return false;
    const now = new Date();
    const s = new Date(currentCycle.submissionStart);
    const e = new Date(currentCycle.submissionEnd);
    e.setHours(23, 59, 59, 999);
    return now >= s && now <= e;
  })();

  const ctaDisabled = isWindowless
    ? false
    : isAdminLocked || (!isSubmissionWindowOpen && !hasGoalsInCurrentCycle);

  const isViewMode = !isWindowless && (
    (!isSubmissionWindowOpen && hasGoalsInCurrentCycle) ||
    (maxGoals > 0 && goalsCount >= maxGoals) ||
    areGoalsEvaluated ||
    isEvaluationWindowActive
  );

  const ctaLabel = isViewMode ? 'View Your Goals' : (hasGoalsInCurrentCycle ? 'Open Your Goals' : 'Add New Goals');
  const ctaIcon = isViewMode ? <BarChart3 size={15} /> : <Plus size={15} />;

  const ctaTooltip = isAdminLocked
    ? 'As org admin, your goals skip manager approval — turn on Unlock editing to manage them'
    : !isSubmissionWindowOpen && !hasGoalsInCurrentCycle
      ? 'The add-goals window is closed. No goals have been added.'
      : isViewMode
        ? 'View goals for this month'
        : 'Add new goals';

  const handleCta = () => {
    if (ctaDisabled) return;
    const activeMonth = (months || []).find(m => m.status === 'active');
    if (activeMonth && onMonthClick) onMonthClick(activeMonth.name);
    else if (currentCycle && onMonthClick) onMonthClick(currentCycle.month);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[3], fontFamily: t.font.family }}>
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: t.s[4], flexWrap: 'wrap',
        }}
      >
        {/* Left: cycle badge + title + phase stepper */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: t.s[2], minWidth: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: t.s[2] }}>
            <Badge tone="brand" dot>{cyclePillText}</Badge>
            {isWindowless && (
              <Badge tone="neutral">{flowMode === 'checklist' ? 'Checklist mode' : 'Continuous mode'}</Badge>
            )}
          </span>

          <h2 style={{ margin: 0, fontSize: t.font.size.xl, fontWeight: t.font.weight.semibold, color: t.c.text }}>
            {headerTitle}
          </h2>

          {/* Phase stepper — where the month is in its lifecycle */}
          {steps.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: t.s[2], marginTop: 2 }}>
              {steps.map((step, i) => {
                const isActive = step.state === 'active';
                const isDone = step.state === 'done';
                const fg = isActive ? t.c.primary : isDone ? t.status.positive : t.c.textTertiary;
                return (
                  <React.Fragment key={step.key}>
                    {i > 0 && <span style={{ width: 14, height: 1, background: t.c.border, flexShrink: 0 }} />}
                    <Tooltip title={`${formatDate(step.start)} – ${formatDate(step.end)}`}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'default' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 16, height: 16, borderRadius: 99, flexShrink: 0,
                          fontSize: 10, fontWeight: t.font.weight.semibold, fontVariantNumeric: 'tabular-nums',
                          background: isActive ? t.c.primary : isDone ? `${t.status.positive}22` : t.c.surfaceMuted,
                          color: isActive ? '#fff' : fg,
                          border: isActive || isDone ? 'none' : `1px solid ${t.c.border}`,
                        }}>
                          {isDone ? <Check size={10} strokeWidth={3} /> : i + 1}
                        </span>
                        <span style={{
                          fontSize: t.font.size.sm,
                          fontWeight: isActive ? t.font.weight.semibold : t.font.weight.regular,
                          color: isActive ? t.c.text : t.c.textSecondary,
                          whiteSpace: 'nowrap',
                        }}>
                          {step.label}
                        </span>
                        {isActive && step.daysLeft !== null && (
                          <span style={{
                            fontSize: t.font.size.xs, fontWeight: t.font.weight.medium,
                            color: step.daysLeft <= 3 ? t.status.negative : t.c.primary,
                            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                          }}>
                            {step.daysLeft === 0 ? 'ends today' : `${step.daysLeft}d left`}
                          </span>
                        )}
                      </span>
                    </Tooltip>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: avg score + CTA. Checklist mode has no scores at all. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: t.s[3], flexWrap: 'wrap' }}>
          {flowMode !== 'checklist' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2] }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: t.status.info }} />
              <span style={{ fontSize: t.font.size['2xl'], fontWeight: t.font.weight.semibold, color: t.c.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {scoreDisplay}
                <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.regular, color: t.c.textTertiary }}>/{scoreDenominator}</span>
              </span>
              <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>Avg score</span>
            </div>
          )}

          <Tooltip title={ctaTooltip}>
            <span>
              <Button variant="primary" leftIcon={ctaIcon} onClick={handleCta} disabled={ctaDisabled}>
                {ctaLabel}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* "Your move" — the single next action, so nobody has to decode windows */}
      {yourMove && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: t.s[2],
          padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.md,
          background: `${t.c.primary}14`,
          border: `1px solid ${t.c.primary}33`,
        }}>
          <Sparkles size={13} style={{ color: t.c.primary, flexShrink: 0 }} />
          <span style={{ fontSize: t.font.size.sm, color: t.c.text }}>
            <span style={{ fontWeight: t.font.weight.semibold }}>Your move: </span>
            {yourMove}
          </span>
        </div>
      )}
    </div>
  );
};

export default GoalsHeader;
